import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../lib/api";
import * as Icons from "lucide-react";
import { ArrowRight, Sparkles, Rocket, Code2 } from "lucide-react";
import { useReadThemeCtx } from "../lib/ReadThemeContext";

// Hand-picked "key links" that anchor the docs landing page.
const KEY_LINKS = [
  { section: "introduction", slug: "overview", icon: Sparkles, eyebrow: "Start here", note: "What T-REX is and why institutions use it." },
  { section: "t-rex-network", slug: "t-rex-ledger", icon: Rocket, eyebrow: "Network", note: "The institutional reference ledger." },
  { section: "developers", slug: "getting-started", icon: Code2, eyebrow: "Build", note: "Quickstart for developers." },
];

export default function DocsHome() {
  const [tree, setTree] = useState({ sections: [] });
  const [loading, setLoading] = useState(true);
  const { theme } = useReadThemeCtx();
  const isLight = theme === "light";

  useEffect(() => {
    api.get("/docs")
      .then((r) => setTree(r.data))
      .finally(() => setLoading(false));
  }, []);

  // Resolve hand-picked key links against the live tree
  const pagesIndex = useMemo(() => {
    const idx = new Map();
    for (const s of tree.sections || []) {
      for (const p of s.pages || []) idx.set(`${s.slug}/${p.slug}`, { section: s, page: p });
    }
    return idx;
  }, [tree]);

  const resolvedKeyLinks = KEY_LINKS
    .map((k) => ({ ...k, resolved: pagesIndex.get(`${k.section}/${k.slug}`) }))
    .filter((k) => !!k.resolved);

  // Tone-specific tokens
  const titleColor = isLight ? "text-aubergine" : "text-bone";
  const bodyColor = isLight ? "text-aubergine/70" : "text-bone/70";
  const eyebrow = "text-champagne"; // works on both surfaces
  const eyebrowMuted = isLight ? "text-aubergine/55" : "text-bone/55";

  const keyCard = isLight
    ? "border-aubergine/12 bg-bone-soft hover:border-champagne hover:bg-bone"
    : "border-champagne/30 bg-aubergine-tier hover:border-champagne hover:bg-aubergine-raised";
  const sectionCard = isLight
    ? "border-aubergine/10 bg-bone-soft"
    : "border-champ-light/10 bg-aubergine-tier";
  const cardHeading = isLight ? "text-aubergine" : "text-bone";
  const cardBody = isLight ? "text-aubergine/65" : "text-bone/65";
  const pageLink = isLight ? "text-aubergine/70 hover:text-champagne" : "text-bone/70 hover:text-champagne";
  const moreColor = isLight ? "text-aubergine/45" : "text-bone/45";

  return (
    <div data-testid="docs-home">
      <Helmet>
        <title>Documentation · T-REX Network</title>
        <meta name="description" content="Official documentation for T-REX Network. The institutional reference ledger for tokenised real-world assets." />
      </Helmet>

      {/* Hero */}
      <header className="mb-14">
        <p className={`font-mono text-[11px] uppercase tracking-[0.2em] mb-3 ${eyebrow}`}>Documentation</p>
        <h1 className={`trex-h1 leading-[1.05] ${titleColor}`}>
          T-REX <em className="accent-italic">documentation</em>.
        </h1>
        <p className={`mt-5 text-base lg:text-lg leading-relaxed max-w-2xl ${bodyColor}`}>
          The institutional reference ledger for tokenised real-world assets. Built on ERC-3643. Combining compliance, identity, and orchestration in a single coordination layer.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link to="/docs/introduction/overview" className={isLight ? "trex-btn-primary" : "inline-flex items-center justify-center rounded-md bg-champagne text-aubergine hover:bg-champ-light px-5 py-2.5 font-mono text-xs h-10 tracking-[0.04em]"} data-testid="docs-home-cta-overview">
            Read the overview<ArrowRight className="ml-2 h-3.5 w-3.5" />
          </Link>
          <Link to="/docs/developers/getting-started" className={isLight ? "trex-btn-ghost" : "inline-flex items-center justify-center rounded-md border px-5 py-2.5 font-mono text-xs h-10 tracking-[0.04em] border-champagne text-champagne hover:bg-champagne/10"} data-testid="docs-home-cta-developers">
            Get started building
          </Link>
        </div>
      </header>

      {/* Key links */}
      {resolvedKeyLinks.length > 0 && (
        <section className="mb-14" data-testid="docs-home-key-links">
          <p className={`font-mono text-[10px] uppercase tracking-[0.15em] mb-4 ${eyebrowMuted}`}>Key links</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {resolvedKeyLinks.map((k) => {
              const Icon = k.icon;
              return (
                <Link
                  key={`${k.section}/${k.slug}`}
                  to={`/docs/${k.section}/${k.slug}`}
                  data-testid={`docs-home-keylink-${k.section}-${k.slug}`}
                  className={`group flex flex-col rounded-lg border p-5 transition-colors ${keyCard}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-champagne" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-champagne">{k.eyebrow}</span>
                  </div>
                  <h3 className={`mt-3 text-lg font-medium ${cardHeading}`}>{k.resolved.page.title}</h3>
                  <p className={`mt-1.5 text-sm ${cardBody}`}>{k.note}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-champagne group-hover:text-aubergine/80">
                    Read <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* All sections */}
      <section>
        <p className={`font-mono text-[10px] uppercase tracking-[0.15em] mb-4 ${eyebrowMuted}`}>All sections</p>
        {loading ? (
          <p className={`text-sm ${bodyColor}`}>Loading…</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="docs-home-sections">
            {tree.sections.map((s) => {
              const Icon = Icons[s.icon] || Icons.Folder;
              return (
                <div key={s.slug} className={`rounded-lg border p-6 flex flex-col ${sectionCard}`} data-testid={`docs-home-section-${s.slug}`}>
                  <Icon className="h-5 w-5 text-champagne" />
                  <h2 className={`mt-4 text-lg font-medium ${cardHeading}`}>{s.title}</h2>
                  <ul className="mt-4 space-y-1.5 flex-1">
                    {s.pages.slice(0, 6).map((p) => (
                      <li key={p.page_id}>
                        <Link to={`/docs/${s.slug}/${p.slug}`} className={`text-sm transition-colors ${pageLink}`} data-testid={`docs-home-page-link-${s.slug}-${p.slug}`}>
                          {p.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  {s.pages.length > 6 && (
                    <p className={`mt-3 font-mono text-[10px] uppercase tracking-[0.12em] ${moreColor}`}>
                      +{s.pages.length - 6} more
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
